import React, { useEffect, useState } from 'react';
import { get } from 'lodash';
import { SvgIcon } from 'core/icons';
import { Translate, useTranslate } from 'core/translate';
import { withNotifications } from '../../notifications/NotificationsContext';
import SlideImage from './SlideImage';
import useCompareCarousel, {
  getNextIndex,
  getPreviousIndex,
} from './useCompareCarousel';
import { combineClassNames } from '../../../../core/css';

function makeIndices(start, delta) {
  const indices = [];

  while (indices.length < 1) {
    indices.push(start);
    start += delta;
  }

  return indices;
}

const SlidePriceItem = ({ msrp, onOpenDisclaimer, tabable, primaryText }) => {
  const translate = useTranslate();
  return (
    <div className="header-item__price">
      <label className="header-item__price-label">
        <Translate value="compare.carousel.msrp.label.v3" />{' '}
      </label>
      <button
        className="header-item__price-total"
        onClick={onOpenDisclaimer}
        tabIndex={tabable ? '0' : '-1'}
        aria-hidden={tabable ? 'false' : 'true'}
        aria-label={translate('compare.msrp.aria.v3', { primaryText, msrp })}>
        <span>{msrp}</span>
        <span className="header-item__total-dagger">†</span>
      </button>
    </div>
  );
};

const Slide = ({
  slideData,
  isSorted,
  isScrolled,
  onOpenDisclaimer,
  tabable = false,
}) => {
  const { primaryText, msrp, imageUrl } = slideData;
  return (
    <table role="table">
      <thead role="rowgroup">
        {/* Included empty tr to appease accessibility scans in cases where the header-item tr below is aria-hidden */}
        <tr role="row">
          <th>
            <div role="columnheader" />
          </th>
        </tr>
        <tr
          aria-hidden={tabable ? 'false' : 'true'}
          key={primaryText}
          className={combineClassNames(
            'header-item',
            isSorted ? 'header-item--sorted' : ''
          )}
          role="row">
          <th>
            {imageUrl && (
              <SlideImage
                cgi={imageUrl}
                isScrolled={isScrolled}
                alt={primaryText}
              />
            )}
            <div
              className="header-item__name"
              role="columnheader"
              aria-sort={isSorted ? 'ascending' : 'false'}
              aria-hidden={tabable ? 'false' : 'true'}
              dangerouslySetInnerHTML={{
                __html: primaryText,
              }}
            />
            {msrp && (
              <SlidePriceItem
                onOpenDisclaimer={onOpenDisclaimer}
                msrp={msrp}
                tabable={tabable}
                primaryText={primaryText}
              />
            )}
          </th>
        </tr>
      </thead>
    </table>
  );
};

// TODO: Move state up to Compare component? (indexing logic should go in there so we can just handle next / prev etc)
const CompareCarousel = ({
  onSlideChange,
  columnIndex,
  isStatic,
  slideDataList,
  notifications,
  isScrolledNav,
  hideFromTab,
  columnOption: { activeIndex, goalIndex, isSorted },
  analyticsKey,
  itemDescription,
}) => {
  const { show } = notifications;
  const translate = useTranslate();
  const [nextItemDescription, setNextItemDescription] = useState(null);

  const onOpenDisclaimer = (event) => {
    event.preventDefault();
    show(
      translate('byo.page.general.disclaimer.msrp.simple'),
      event,
      null,
      true
    );
  };

  const alertNextSlide = (index) => {
    const nextSlideItem = slideDataList[index]?.primaryText;
    if (nextSlideItem) {
      setNextItemDescription(
        `Showing ${nextSlideItem} in column ${columnIndex + 1}`
      );
    }
  };

  const [handlers, style, next, previous, jumpToSlide] = useCompareCarousel(
    slideDataList.length,
    isStatic,
    (val, animateNext) => {
      if (goalIndex === activeIndex && val !== activeIndex) {
        alertNextSlide(val);
        onSlideChange(columnIndex, val, animateNext);
      }
    },
    {
      offset: 0,
      desired: activeIndex,
      active: activeIndex,
      nextWasLast: true,
      smooth: true,
    }
  );

  const beforeIndices = makeIndices(slideDataList.length - 1, -1);
  const afterIndices = makeIndices(0, 1);

  useEffect(() => {
    jumpToSlide(activeIndex, !isStatic);
  }, [activeIndex]);

  const activeTrimName = get(slideDataList[activeIndex], 'primaryText');

  return (
    <div className="compare-carousel">
      {!isStatic && (
        <>
          <div
            className="compare-carousel-nav__button-container compare-carousel-nav__button-container--left stat-arrow-prev"
            data-dtm={`${analyticsKey}:${activeTrimName}`}>
            <button
              onClick={previous}
              className="compare-carousel-nav__button"
              tabIndex={hideFromTab ? '-1' : '0'}
              aria-hidden={hideFromTab ? 'true' : 'false'}
              aria-label={translate('compare.slide.arrow.aria.v3', {
                index: activeIndex + 1,
                trim:
                  slideDataList[
                    getPreviousIndex(slideDataList.length, activeIndex)
                  ].primaryText,
                itemDescription,
              })}>
              <SvgIcon
                name="chevron-compare-left"
                className="compare-carousel-nav__chevron"
              />
            </button>
          </div>
          <div
            className="compare-carousel-nav__button-container compare-carousel-nav__button-container--right stat-arrow-next"
            data-dtm={`${analyticsKey}:${activeTrimName}`}>
            <button
              onClick={next}
              className="compare-carousel-nav__button"
              tabIndex={hideFromTab ? '-1' : '0'}
              aria-hidden={hideFromTab ? 'true' : 'false'}
              aria-label={translate('compare.slide.arrow.aria.v3', {
                index: activeIndex + 1,
                trim:
                  slideDataList[getNextIndex(slideDataList.length, activeIndex)]
                    .primaryText,
                itemDescription,
              })}>
              <SvgIcon
                name="chevron-compare-right"
                className="compare-carousel-nav__chevron"
              />
            </button>
          </div>
        </>
      )}
      <div className="compare-carousel__content" {...handlers} style={style}>
        {beforeIndices.map((i) => (
          <div key={i} className="compare-carousel__slide">
            <Slide
              slideData={slideDataList[i]}
              isSorted={isSorted}
              isScrolled={isScrolledNav}
              onOpenDisclaimer={onOpenDisclaimer}
            />
          </div>
        ))}
        {slideDataList.map((slideData, i) => (
          <div key={i} className="compare-carousel__slide">
            <Slide
              slideData={slideData}
              isSorted={isSorted}
              isScrolled={isScrolledNav}
              onOpenDisclaimer={onOpenDisclaimer}
              tabable={
                Math.abs(parseFloat(style.left) / 100) - 1 === i && !hideFromTab
              }
            />
          </div>
        ))}
        {afterIndices.map((i) => (
          <div key={i} className="compare-carousel__slide">
            <Slide
              slideData={slideDataList[i]}
              isSorted={isSorted}
              isScrolled={isScrolledNav}
              onOpenDisclaimer={onOpenDisclaimer}
            />
          </div>
        ))}
      </div>
      {nextItemDescription && (
        <div
          className="compare-carousel-nav__aria-announcements"
          role="status"
          aria-live="assertive"
          aria-atomic="true">
          {nextItemDescription}
        </div>
      )}
    </div>
  );
};

export default withNotifications(CompareCarousel);
